import { useTranslation } from "react-i18next";

import { getDeskSensorStatus } from "@lib/logic";
import { getSensorLastUpdatedText, getWorkspaceOccupancyForBuildingAndFloorId, useAppSelector } from "@lib/store";
import { Icon } from "@atoms";
import { cn } from "@utils";

type PersonDeskStatusProps = {
  buildingId: string;
  floorId: string;
  deskId: string;
};

export const PersonDeskStatus = ({ buildingId, floorId, deskId }: PersonDeskStatusProps) => {
  const { t } = useTranslation();
  const sensorData = useAppSelector(state => getWorkspaceOccupancyForBuildingAndFloorId(state, buildingId, floorId));
  const deskStatus = getDeskSensorStatus(sensorData, deskId);

  if (!deskStatus) return null;

  const isOccupied = deskStatus.status === "occupied";
  const detection = isOccupied ? t("hereAndNow:PresenceDetected") : t("hereAndNow:NoPresenceDetected");
  const updated = getSensorLastUpdatedText(t, deskStatus.timestamp);

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-[20px] font-semibold leading-[25px]">{t("hereAndNow:DeskStatus")}</h3>

      <div className="flex items-start gap-4">
        <div className="relative flex size-12 flex-none items-center justify-center rounded-lg bg-mapiq-black-800*/[0.04]">
          <Icon
            aria-hidden
            icon="broadcast"
            size="24px"
            color="currentColor"
          />
          <span
            aria-hidden
            className={cn("absolute right-2.5 top-2.5 size-2.5 rounded-full border-2 border-white", {
              "bg-functional-red": isOccupied,
              "bg-functional-green": !isOccupied,
            })}
          />
        </div>

        <div className="flex min-w-0 flex-col">
          <h4 className="text-[15px] font-semibold leading-5">
            {isOccupied ? t("hereAndNow:DeskOccupied") : t("hereAndNow:DeskFree")}
          </h4>
          <p className="text-mapiq-black-500">{[detection, updated].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
    </section>
  );
};
